// utils/collectionStorageManager.js

/**
 * Gestionnaire centralisé du localStorage pour les collections
 * Gère le nettoyage automatique selon le contexte de navigation
 */

// Liste exhaustive de toutes les clés localStorage utilisées dans le workflow collections
const COLLECTION_STORAGE_KEYS = [
  // Mode et état général
  "collectionMode",
  "collectionCreateState",
  "collectionWasEdited",

  // Articles sélectionnés (formats multiples)
  "selectedCollectionArticles",
  // Note: selectedCollectionArticlesData et selectedCollectionArticleIds sont maintenant 
  // entièrement gérés par le store Vuex et ne sont plus stockés dans localStorage

  // Compteurs pour détection de changements
  "previousArticleCountBeforeSelection",

  // Filtres (contextes multiples)
  "articleSelectorActiveFilters",
  "collectionFilters",
  "manualCollectionFilters",
  "initialFilterValues",
  "editingCollectionFilters",
  "collectionRawFilters",

  // Édition de collection existante
  "editingCollectionId",
  "editedCollectionMode",
  "editedCollectionArticleIds",
  "editedCollectionArticlesData",
  "editedCollectionFilters",
  "editedCollectionSort",

  // Images
  "collectionImageBase64",
  "collectionImageMetadata",
];

// Pages qui font partie du workflow collections
const COLLECTION_WORKFLOW_ROUTES = [
  "client-collections", // Liste des collections
  "client-collections-create", // Création de collection
  "client-collections-details", // Détails/édition de collection
  "article-selector", // Sélection d'articles
];

class CollectionStorageManager {
  /**
   * Nettoie toutes les clés liées aux collections
   * @param {Array} excludeKeys - Clés à exclure du nettoyage (optionnel)
   */
  static clearAll(excludeKeys = []) {

    // Nettoyer les clés fixes
    COLLECTION_STORAGE_KEYS.forEach((key) => {
      if (!excludeKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Nettoyer les clés dynamiques (avec brandId/collectionId)
    this.clearDynamicKeys(excludeKeys);
  }

  /**
   * Nettoie les clés dynamiques (contenant des IDs)
   */
  static clearDynamicKeys(excludeKeys = []) {
    const dynamicPatterns = [
      /^selectedArticles_\d+_\d+$/, // selectedArticles_{brandId}_{collectionId}
      // selectedArticlesData_ pattern retiré car migré vers le store Vuex
      /^collection_mode_\d+$/, // collection_mode_{collectionId}
      /^collection_filters_\d+$/, // collection_filters_{collectionId}
    ];

    // Parcourir tout le localStorage pour trouver les clés dynamiques
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      const shouldRemove =
        dynamicPatterns.some((pattern) => pattern.test(key)) &&
        !excludeKeys.includes(key);

      if (shouldRemove) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Nettoie spécifiquement les clés d'une collection donnée
   * @param {String|Number} brandId - ID de la marque
   * @param {String|Number} collectionId - ID de la collection
   */
  static clearSpecificCollectionKeys(brandId, collectionId) {
    const specificKeys = [
      `selectedArticles_${brandId}_${collectionId}`,
      // selectedArticlesData_ key retiré car migré vers le store Vuex
      `collection_mode_${collectionId}`,
      `collection_filters_${collectionId}`,
    ];

    specificKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Vérifie si une route fait partie du workflow collections
   * @param {String} routeName - Nom de la route
   * @returns {Boolean}
   */
  static isCollectionWorkflowRoute(routeName) {
    return COLLECTION_WORKFLOW_ROUTES.includes(routeName);
  }

  /**
   * Nettoyage conditionnel selon la navigation
   * @param {Object} to - Route de destination
   * @param {Object} from - Route d'origine
   */
  static handleRouteChange(to, from) {
    const isLeavingWorkflow =
      this.isCollectionWorkflowRoute(from?.name) &&
      !this.isCollectionWorkflowRoute(to?.name);

    if (isLeavingWorkflow) {
      
      this.clearAll();
      return true;
    }

    return false;
  }

  /**
   * Nettoyage partiel pour transitions internes
   * @param {String} context - Contexte du nettoyage ('creation-success', 'cancel-creation', etc.)
   * @param {Object} options - Options supplémentaires (brandId, collectionId, etc.)
   */
  static clearForContext(context, options = {}) {
    switch (context) {
      case "creation-success": {
        // Après création réussie, nettoyer toutes les clés de création mais garder l'état de base
        this.clearAll(["collectionMode"]);
        break;
      }

      case "cancel-creation": {
        // Annulation complète de création - tout nettoyer
        this.clearAll();
        break;
      }

      case "update-success": {
        // Après mise à jour réussie, nettoyer tout sauf les données persistantes
        const keysToKeep = [];
        this.clearAll(keysToKeep);
        break;
      }

      case "edit-collection": {
        // Avant édition d'une collection, nettoyer les anciennes clés spécifiques
        if (options.brandId && options.collectionId) {
          this.clearSpecificCollectionKeys(
            options.brandId,
            options.collectionId
          );
        }
        break;
      }

      case "cancel-edit": {
        // Annulation d'édition, nettoyer les clés spécifiques de la collection
        if (options.brandId && options.collectionId) {
          this.clearSpecificCollectionKeys(
            options.brandId,
            options.collectionId
          );
        }
        // Nettoyer aussi les autres clés d'édition
        const editKeys = [
          "editingCollectionId",
          "editedCollectionSort",
          "collectionWasEdited",
        ];
        editKeys.forEach((key) => localStorage.removeItem(key));
        break;
      }

      case "navigation-within-workflow": {
        // Navigation interne - nettoyer les clés spécifiques de l'ancienne collection
        if (options.fromBrandId && options.fromCollectionId) {
          this.clearSpecificCollectionKeys(
            options.fromBrandId,
            options.fromCollectionId
          );
        }
        break;
      }

      default: {
        break;
      }
    }
  }

  /**
   * Sauvegarde de sécurité avant nettoyage (pour debug)
   */
  static createBackup() {
    const backup = {};
    COLLECTION_STORAGE_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        backup[key] = value;
      }
    });

    // Sauvegarder aussi les clés dynamiques
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.includes("selectedArticles_")) {
        // Note: selectedArticlesData_ n'est plus utilisé car migré vers le store Vuex
        backup[key] = localStorage.getItem(key);
      }
    }
    return backup;
  }

  /**
   * Restauration depuis une sauvegarde
   */
  static restoreFromBackup(backup) {
    Object.entries(backup).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }

  /**
   * Détecte les conflits potentiels entre clés génériques et spécifiques
   * @param {String|Number} brandId - ID de la marque
   * @param {String|Number} collectionId - ID de la collection
   */
  static detectConflicts(brandId, collectionId) {
    const conflicts = [];

    // Vérifier si les clés génériques et spécifiques coexistent
    const hasGeneric =
      localStorage.getItem("selectedCollectionArticles") !== null;
    const hasSpecific =
      localStorage.getItem(`selectedArticles_${brandId}_${collectionId}`) !==
      null;

    if (hasGeneric && hasSpecific) {
      conflicts.push({
        type: "dual-storage",
        message: "Clés génériques ET spécifiques présentes simultanément",
        generic: "selectedCollectionArticles",
        specific: `selectedArticles_${brandId}_${collectionId}`,
      });
    }

    // Vérifier les données orphelines (clés spécifiques d'autres collections)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const dynamicPattern = /^selectedArticles_(\d+)_(\d+)$/;
      const match = key.match(dynamicPattern);

      if (match) {
        const [, keyBrandId, keyCollectionId] = match;
        if (
          keyBrandId !== brandId.toString() ||
          keyCollectionId !== collectionId.toString()
        ) {
          conflicts.push({
            type: "orphaned-data",
            message: `Données orphelines détectées`,
            key: key,
            belongs_to: `Brand ${keyBrandId}, Collection ${keyCollectionId}`,
            current_context: `Brand ${brandId}, Collection ${collectionId}`,
          });
        }
      }
    }

    return conflicts;
  }
}

export default CollectionStorageManager;
